import React, {useEffect} from "react";
import Button from "@mui/joy/Button";
import Stack from "@mui/joy/Stack";
import IconButton from "@mui/joy/IconButton";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Sheet from "@mui/joy/Sheet";
import Input from "@mui/joy/Input";
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import {Banner} from "../../models/banner";
import ImageUploading, {ImageListType} from "react-images-uploading";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {gamesListApi} from "../games/gamesApi";
import {NotificationService} from "../../common/notification";
import {Game} from "../../models/games";
import Select from "@mui/joy/Select";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import Option from "@mui/joy/Option";
import CircularProgress from "@mui/joy/CircularProgress";
import Checkbox from "@mui/joy/Checkbox";

type DefaultProps = {
    banner: Banner
    open: boolean
    setOpen: (e: boolean) => void
    onConfirm: (banner: Banner, fileRu?: File, fileEn?: File, fileMobileRu?: File, fileMobileEn?: File) => Promise<boolean>
}

const EditorDialog = (props: DefaultProps) => {
    const {banner, open, setOpen, onConfirm} = props;
    const [games, setGames] = React.useState<Game[]>([]);
    const [loadedGames, setLoadedGames] = React.useState(false);

    const [cellCount, setCellCount] = React.useState(banner.cellCount);
    const [canClose, setCanClose] = React.useState(banner.canClose);
    const [isActive, setIsActive] = React.useState(banner.isActive);
    const [redirectUrl, setRedirectUrl] = React.useState(banner.redirectUrl);
    const [gameId, setGameId] = React.useState(banner.gameId);

    const [imageRu, setImageRu] = React.useState<ImageListType>([]);
    const [imageFullRuSize, setImageRuFullSize] = React.useState('0');

    const [imageEn, setImageEn] = React.useState<ImageListType>([]);
    const [imageFullEnSize, setImageEnFullSize] = React.useState('0');

    const [imageMobileRu, setImageMobileRu] = React.useState<ImageListType>([]);
    const [imageMobileRuSize, setImageMobileRuSize] = React.useState('0');

    const [imageMobileEn, setImageMobileEn] = React.useState<ImageListType>([]);
    const [imageMobileEnSize, setImageMobileEnSize] = React.useState('0');
    React.useEffect(() => {
        loadGames().catch(console.error);
    }, [])

    const loadGames = async () => {
        try {
            let result = await gamesListApi();
            if (result.result) {
                setGames(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoadedGames(true)
        }
    }


    useEffect(() => {
        if (imageRu.length > 0) {
            setImageRuFullSize(new Intl.NumberFormat().format(imageRu[0].file!.size / 1024 / 1024));
        } else if (banner && banner.urlRu) {
            fetch(banner.urlRu, {method: 'HEAD'})
                .then(r => setImageRuFullSize(new Intl.NumberFormat().format(Number(r.headers.get('Content-Length')) / 1024 / 1024)));
        }

        if (imageEn.length > 0) {
            setImageEnFullSize(new Intl.NumberFormat().format(imageEn[0].file!.size / 1024 / 1024));
        } else if (banner && banner.urlEn) {
            fetch(banner.urlEn, {method: 'HEAD'})
                .then(r => setImageEnFullSize(new Intl.NumberFormat().format(Number(r.headers.get('Content-Length')) / 1024 / 1024)));
        }

        if (imageMobileRu.length > 0) {
            setImageMobileRuSize(new Intl.NumberFormat().format(imageMobileRu[0].file!.size / 1024 / 1024));
        } else if (banner && banner.urlRuMobile) {
            fetch(banner.urlRuMobile, {method: 'HEAD'})
                .then(r => setImageMobileRuSize(new Intl.NumberFormat().format(Number(r.headers.get('Content-Length')) / 1024 / 1024)));
        }

        if (imageMobileEn.length > 0) {
            setImageMobileEnSize(new Intl.NumberFormat().format(imageMobileEn[0].file!.size / 1024 / 1024));
        } else if (banner && banner.urlEnMobile) {
            fetch(banner.urlEnMobile, {method: 'HEAD'})
                .then(r => setImageMobileEnSize(new Intl.NumberFormat().format(Number(r.headers.get('Content-Length')) / 1024 / 1024)));
        }
    }, [banner, imageRu, imageEn, imageMobileRu, imageMobileEn]);

    return (
        <Modal open={open} onClose={() => setOpen(false)}>
            <ModalDialog>
                <ModalClose/>
                <Typography id="filter-modal" level="h2">
                    {banner.id > 0 ? 'Редактировать банер' : 'Новый банер'}
                </Typography>
                <Divider sx={{my: 2}}/>
                <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', overflowX: 'hidden'}}>
                    <FormControl>
                        <FormLabel>Ru банер</FormLabel>
                        <ImageUploading
                            value={imageRu}
                            onChange={(e) => setImageRu(e)}
                            maxNumber={1}
                        >
                            {({
                                  onImageUpload,
                                  onImageUpdate,
                              }) => (
                                <div className="upload__image-wrapper">
                                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                        <img src={imageRu.length === 1 ? imageRu[0].dataURL : banner?.urlRu}
                                             title={`${imageFullRuSize} Mb`}
                                             alt="" width={50 * cellCount} height="50"/>
                                        <IconButton color="primary"
                                                    onClick={() => imageRu.length === 1 ? onImageUpdate(0) : onImageUpload()}>
                                            <AddPhotoAlternateIcon/>
                                        </IconButton>
                                    </Stack>
                                </div>
                            )}
                        </ImageUploading>
                    </FormControl>
                    <FormControl>
                        <FormLabel>En банер</FormLabel>
                        <ImageUploading
                            value={imageEn}
                            onChange={(e) => setImageEn(e)}
                            maxNumber={1}
                        >
                            {({
                                  onImageUpload,
                                  onImageUpdate,
                              }) => (
                                <div className="upload__image-wrapper">
                                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                        <img src={imageEn.length === 1 ? imageEn[0].dataURL : banner?.urlEn}
                                             title={`${imageFullEnSize} Mb`}
                                             alt="" width={50 * cellCount} height="50"/>
                                        <IconButton color="primary"
                                                    onClick={() => imageEn.length === 1 ? onImageUpdate(0) : onImageUpload()}>
                                            <AddPhotoAlternateIcon/>
                                        </IconButton>
                                    </Stack>
                                </div>
                            )}
                        </ImageUploading>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Мобильный Ru банер</FormLabel>
                        <ImageUploading
                            value={imageMobileRu}
                            onChange={(e) => setImageMobileRu(e)}
                            maxNumber={1}
                        >
                            {({
                                  onImageUpload,
                                  onImageUpdate,
                              }) => (
                                <div className="upload__image-wrapper">
                                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                        <img src={imageMobileRu.length === 1 ? imageMobileRu[0].dataURL : banner?.urlRuMobile}
                                             title={`${imageMobileRuSize} Mb`}
                                             alt="" width="100" height="50"/>
                                        <IconButton color="primary"
                                                    onClick={() => imageMobileRu.length === 1 ? onImageUpdate(0) : onImageUpload()}>
                                            <AddPhotoAlternateIcon/>
                                        </IconButton>
                                    </Stack>
                                </div>
                            )}
                        </ImageUploading>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Мобильный En банер</FormLabel>
                        <ImageUploading
                            value={imageMobileEn}
                            onChange={(e) => setImageMobileEn(e)}
                            maxNumber={1}
                        >
                            {({
                                  onImageUpload,
                                  onImageUpdate,
                              }) => (
                                <div className="upload__image-wrapper">
                                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                        <img src={imageMobileEn.length === 1 ? imageMobileEn[0].dataURL : banner?.urlEnMobile}
                                             title={`${imageMobileEnSize} Mb`}
                                             alt="" width="100" height="50"/>
                                        <IconButton color="primary"
                                                    onClick={() => imageMobileEn.length === 1 ? onImageUpdate(0) : onImageUpload()}>
                                            <AddPhotoAlternateIcon/>
                                        </IconButton>
                                    </Stack>
                                </div>
                            )}
                        </ImageUploading>
                    </FormControl>
                    <FormControl sx={{flexGrow: 1}}>
                        <FormLabel>Игра</FormLabel>
                        <Select
                            size="sm"
                            startDecorator={<VideogameAssetIcon/>}
                            defaultValue={gameId}
                            value={gameId}
                            onChange={(_, value) => setGameId(value ?? -1)}
                        >
                            {loadedGames ? <>
                                    <Option key={0} value={0}>Игра</Option>
                                    {games.map((p) =>
                                        <Option key={p.id} value={p.id}>{p.name}</Option>
                                    )}
                                </> :
                                <CircularProgress/>
                            }
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Ширина</FormLabel>
                        <Input placeholder="ширина" value={cellCount}
                               onChange={(event) => setCellCount(Number(event.target.value))}/>
                    </FormControl>
                    <FormControl>
                        <Checkbox label="Можно закрыть"
                                  checked={canClose}
                                  onChange={(event) => setCanClose(event.target.checked)}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Активный</FormLabel>
                        <Checkbox label="Активный"
                                  checked={isActive}
                                  onChange={(event) => setIsActive(event.target.checked)}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Ссылка</FormLabel>
                        <Input placeholder="ссылка" value={redirectUrl}
                               onChange={(event) => setRedirectUrl(event.target.value)}/>
                    </FormControl>
                    <Button
                        onClick={() => setOpen(false)}
                        color="neutral"
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={async ()  => {
                            if(await onConfirm({
                                id: banner.id,
                                gameId: gameId,
                                redirectUrl: redirectUrl,
                                canClose: canClose,
                                isActive: isActive,
                                cellCount: cellCount,
                                gameName: banner.gameName,
                                gameIconUrl: banner.gameIconUrl,
                                urlRu: banner.urlRu,
                                urlEn: banner.urlEn,
                                urlRuMobile:banner.urlRuMobile,
                                urlEnMobile:banner.urlEnMobile
                            }, imageRu.length === 1 ? imageRu[0].file : undefined,
                                imageEn.length === 1 ? imageEn[0].file : undefined,
                                imageMobileRu.length === 1 ? imageMobileRu[0].file : undefined,
                                imageMobileEn.length === 1 ? imageMobileEn[0].file : undefined,
                            )) {
                                setOpen(false);
                            }
                        }}
                        color="primary"
                    >
                        Сохранить
                    </Button>
                </Sheet>
            </ModalDialog>
        </Modal>
    );
};

export default EditorDialog;